import React, { useState } from 'react';
import { ContactMessage, PersonalInfo } from '../../types/entityTypes';
import Card from '../ui/Card';
import Button from '../ui/Button';
import { usePortfolio } from '../../contexts/PortfolioContext';
import { ServiceCaller } from '../../api/serviceCaller';

const Contact: React.FC = () => {
  const { data, loading, error } = usePortfolio();

  const [formData, setFormData] = useState<ContactMessage>({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  if (loading) return <div></div>;
  if (error) return <div>Error: {error}</div>;

  const personalInfo: PersonalInfo = data?.personalInfo;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (submitStatus !== 'idle') setSubmitStatus('idle');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Try sending via BE first
      const serviceCaller = new ServiceCaller();
      await serviceCaller.sendEmailRequest(formData.name, formData.email, formData.subject, formData.message);
      setSubmitStatus('success');
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      console.error('Unable to send contact message:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="contact-section py-24 transition-colors duration-300">
      <div className="section-transition" aria-hidden="true"><span>Start a conversation</span></div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="section-heading mb-14">
          <div>
          <span className="section-eyebrow">Conversation / 07</span>
          <h2 className="text-4xl md:text-6xl font-bold text-white mt-3 mb-4 transition-colors duration-300">
            Let’s build something meaningful.
          </h2>
          </div>
          <p className="section-heading-copy">
            The most interesting conversations often begin before the answer is obvious. If you are shaping a product, exploring useful AI, or wrestling with a difficult system, I’d be glad to hear about it.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <Card>
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 transition-colors duration-300">
              Begin with a thought
            </h3>

            <form onSubmit={handleSubmit} className="space-y-6" aria-busy={isSubmitting}>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 transition-colors duration-300">
                    Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    minLength={2}
                    maxLength={80}
                    autoComplete="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-colors duration-300"
                    placeholder="Your Name"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 transition-colors duration-300">
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    maxLength={254}
                    autoComplete="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-colors duration-300"
                    placeholder="your.email@example.com"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 transition-colors duration-300">
                  Subject *
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  required
                  minLength={3}
                  maxLength={150}
                  value={formData.subject}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-colors duration-300"
                  placeholder="What's this about?"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 transition-colors duration-300">
                  Message *
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  minLength={10}
                  maxLength={5000}
                  rows={6}
                  value={formData.message}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-colors duration-300"
                    placeholder="Tell me what you are thinking about, building, or trying to solve..."
                />
              </div>

              {submitStatus === 'success' && (
                <div role="status" className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md">
                  <p className="text-green-800 dark:text-green-300 text-sm">
                    ✅ Thank you! Your message has been sent successfully. I'll get back to you soon.
                  </p>
                </div>
              )}

              {submitStatus === 'error' && (
                <div role="alert" className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                  <p className="text-red-800 dark:text-red-300 text-sm">
                    ❌ The message could not be sent. Please try again or email me directly at {personalInfo.email}.
                  </p>
                </div>
              )}

              <Button
                type="submit"
                variant="primary"
                size="lg"
                disabled={isSubmitting}
                className="w-full"
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </Button>
            </form>
          </Card>

          <div className="conversation-panel">
            <span className="conversation-kicker">Conversations I welcome</span>
            <h3>Bring the unfinished version.</h3>
            <p>You do not need a polished brief. A question, a difficult constraint, or an idea you cannot stop thinking about is enough.</p>
            <div className="conversation-topics"><span>AI & automation</span><span>Product collaboration</span><span>Complex engineering</span><span>Founder conversations</span><span>Interesting opportunities</span></div>
            <div className="conversation-direct"><span>Prefer email?</span><a href={`mailto:${personalInfo.email}`}>{personalInfo.email}</a><small>{personalInfo.location} · Working globally</small></div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
